import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './SelectContactModal.css';
import AppIcon from './icons/AppIcon';

export default function SelectContactModal({ isOpen, onClose, onSelectContact, projectId }) {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [existingContactIds, setExistingContactIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadContacts();
      loadExistingContacts();
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredContacts(contacts);
    } else {
      const filtered = contacts.filter(contact => {
        const nome = contact.nome?.toLowerCase() || '';
        const numero = contact.numero?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return nome.includes(search) || numero.includes(search);
      });
      setFilteredContacts(filtered);
    }
  }, [searchTerm, contacts]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));

      const { data, error } = await supabase
        .from('contatos')
        .select('*')
        .eq('company_id', user.company_id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      setContacts(data || []);
      setFilteredContacts(data || []);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
      alert('Erro ao carregar contatos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingContacts = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));

      // ✅ Buscar contatos que já estão neste projeto (não permite duplicação)
      // Nota: Permite o mesmo contato em projetos diferentes
      const { data, error } = await supabase
        .from('project_items')
        .select('entity_id')
        .eq('project_id', projectId)
        .eq('company_id', user.company_id)
        .eq('entity_type', 'contact');

      if (error) throw error;

      setExistingContactIds(data.map(item => item.entity_id));
    } catch (error) {
      console.error('Erro ao carregar contatos existentes:', error);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    return phone;
  };

  const isContactInProject = (contactId) => {
    return existingContactIds.includes(contactId);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="select-contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Selecionar Contato</h2>
          <button className="btn-close" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="search-box">
          <AppIcon name="search" />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        <div className="contacts-list">
          {loading ? (
            <div className="loading-state">
              <AppIcon name="progress_activity" className="spinning" />
              <p>Carregando contatos...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="empty-state">
              <AppIcon name="person_off" />
              <p>Nenhum contato encontrado</p>
            </div>
          ) : (
            filteredContacts.map(contact => {
              const inProject = isContactInProject(contact.id);
              return (
                <button
                  key={contact.id}
                  className={`contact-item ${inProject ? 'disabled' : ''}`}
                  onClick={() => !inProject && onSelectContact(contact)}
                  disabled={inProject}
                >
                  <div className="contact-avatar">
                    {contact.image_url ? (
                      <img src={contact.image_url} alt={contact.nome || 'Contato'} />
                    ) : (
                      <AppIcon name="person" />
                    )}
                  </div>
                  <div className="contact-info">
                    <h3>{contact.nome || 'Sem nome'}</h3>
                    <p>{formatPhoneNumber(contact.numero)}</p>
                  </div>
                  {inProject && (
                    <span className="badge-in-project">Já no projeto</span>
                  )}
                  {!inProject && (
                    <AppIcon name="arrow_forward" className="arrow" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
